import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  ReactNode,
} from 'react';
import { unstable_batchedUpdates } from 'react-dom';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../AuthContext';

/**
 * State for the unified glass profile sheet (auth popups + profile).
 *
 * The provider is the single owner of open/close state across every page —
 * it replaces the old per-page ProfilePanel state (App-level for /all,
 * BrandPage-local for brand pages). It also owns the `tend-sheet-open` body
 * class that drives the CSS content-push (see src/styles/tend.css and
 * docs/DESIGN_LANGUAGE.md §8 "Dock & push").
 */

export type ProfileSheetView =
  | 'login'
  | 'forgot'
  | 'register'
  | 'profile'
  | 'create-password'
  | 'reset-password'
  | 'api-keys';

interface ProfileSheetContextType {
  isOpen: boolean;
  view: ProfileSheetView;
  /** Token payload for the create-password / reset-password deep-link views. */
  token: string | null;
  openSheet: (view?: ProfileSheetView, payload?: { token?: string | null }) => void;
  closeSheet: () => void;
  /** Trigger-button behavior: opens to profile when authed, login otherwise. */
  toggleSheet: () => void;
  setView: (view: ProfileSheetView) => void;
  /** True when the open sheet docks and pushes page content (desktop). */
  isPushed: boolean;
}

const ProfileSheetContext = createContext<ProfileSheetContextType | undefined>(undefined);

export const useProfileSheet = (): ProfileSheetContextType => {
  const context = useContext(ProfileSheetContext);
  if (!context) {
    throw new Error('useProfileSheet must be used within a ProfileSheetProvider');
  }
  return context;
};

const PUSH_BREAKPOINT = 1024;

interface ProfileSheetProviderProps {
  children: ReactNode;
}

export const ProfileSheetProvider: React.FC<ProfileSheetProviderProps> = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const location = useLocation();

  const [isOpen, setIsOpen] = useState(false);
  const [view, setViewState] = useState<ProfileSheetView>('login');
  const [token, setToken] = useState<string | null>(null);
  const [isDesktop, setIsDesktop] = useState(
    () => window.innerWidth >= PUSH_BREAKPOINT
  );

  useEffect(() => {
    const onResize = () => setIsDesktop(window.innerWidth >= PUSH_BREAKPOINT);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // The body class is what the CSS push rules key off — on app pages via
  // .tend-push-track, on the marketing page via #tend-marketing-root.
  useEffect(() => {
    document.body.classList.toggle('tend-sheet-open', isOpen);
    return () => document.body.classList.remove('tend-sheet-open');
  }, [isOpen]);

  // Close on navigation. Skip the initial mount, and note that this provider
  // effect flushes AFTER child route effects — deep-link routes therefore
  // open the sheet in a rAF (see TokenSheetRoute) so their open lands after
  // this close.
  const prevPathnameRef = React.useRef(location.pathname);
  useEffect(() => {
    if (prevPathnameRef.current !== location.pathname) {
      prevPathnameRef.current = location.pathname;
      setIsOpen(false);
    }
  }, [location.pathname]);

  const openSheet = useCallback(
    (nextView?: ProfileSheetView, payload?: { token?: string | null }) => {
      // Batch explicitly: under legacy ReactDOM.render, updates inside
      // setTimeout (the deep-link open path) are NOT auto-batched — the view
      // would mount one render before the token lands and capture null.
      unstable_batchedUpdates(() => {
        if (nextView) setViewState(nextView);
        if (payload && 'token' in payload) setToken(payload.token ?? null);
        setIsOpen(true);
      });
    },
    []
  );

  const closeSheet = useCallback(() => setIsOpen(false), []);

  const toggleSheet = useCallback(() => {
    setIsOpen((open) => {
      if (!open) setViewState(isAuthenticated ? 'profile' : 'login');
      return !open;
    });
  }, [isAuthenticated]);

  const setView = useCallback((next: ProfileSheetView) => setViewState(next), []);

  const value: ProfileSheetContextType = {
    isOpen,
    view,
    token,
    openSheet,
    closeSheet,
    toggleSheet,
    setView,
    isPushed: isOpen && isDesktop,
  };

  return (
    <ProfileSheetContext.Provider value={value}>
      {children}
    </ProfileSheetContext.Provider>
  );
};
